import React, { useCallback } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import flagPoland from "../../../../../assets/images/flag_poland.svg";
import flagUsa from "../../../../../assets/images/flag_usa.svg";
import formatStrategyBenchmarkRateOfReturn from "../../../../../lib/StrategyBenchmarkChart/formatStrategyBenchmarkRateOfReturn";
import {
  getStrategyDashboardChartMarketFlag,
  STRATEGY_DASHBOARD_CHART_MARKET_FLAG
} from "../../../../../lib/StrategyDashboard/strategyDashboardChartMarketFlags";
import StrategyBenchmarkChartOptionSelect from "./StrategyBenchmarkChartOptionSelect";

const FLAG_SRC = {
  [STRATEGY_DASHBOARD_CHART_MARKET_FLAG.POLAND]: flagPoland,
  [STRATEGY_DASHBOARD_CHART_MARKET_FLAG.USA]: flagUsa
};

const MarketFlag = ({ marketId, className }) => {
  const flagKey = getStrategyDashboardChartMarketFlag(marketId);
  const src = FLAG_SRC[flagKey];

  if (!src) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      width={16}
      height={16}
      className={classNames("shrink-0", className)}
      aria-hidden="true"
    />
  );
};

MarketFlag.propTypes = {
  marketId: PropTypes.string.isRequired,
  className: PropTypes.string
};

const MarketRate = ({ rate, locale, isLoading }) => {
  if (isLoading || rate === null || typeof rate === "undefined") {
    return (
      <span className="text-sm font-semibold leading-130 text-black-8">…</span>
    );
  }

  return (
    <span
      className={classNames(
        "text-sm font-semibold leading-130 whitespace-nowrap shrink-0",
        rate >= 0 ? "text-green-3" : "text-red-3"
      )}
    >
      {formatStrategyBenchmarkRateOfReturn(rate, locale)}
    </span>
  );
};

MarketRate.propTypes = {
  rate: PropTypes.number,
  locale: PropTypes.string.isRequired,
  isLoading: PropTypes.bool
};

const StrategyBenchmarkChartMarketPicker = ({
  locale,
  translate,
  selectedMarket,
  marketOptions,
  onMarketChange
}) => {
  const marketLabel = translate("strategy_benchmark_chart_market_label");
  const triggerLabel = `${marketLabel} ${selectedMarket}`;

  const handleSelect = useCallback(
    (market, close) => {
      onMarketChange(market);
      close();
    },
    [onMarketChange]
  );

  return (
    <StrategyBenchmarkChartOptionSelect
      triggerLabel={triggerLabel}
      ariaLabel={triggerLabel}
      triggerTestId="strategy-benchmark-chart-market-trigger"
      triggerContent={
        <span className="flex flex-col min-w-0">
          <span className="strategy-benchmark-chart__select-label">
            {marketLabel}
          </span>
          <span className="flex items-center gap-2 min-w-0">
            <MarketFlag marketId={selectedMarket} />
            <span className="text-sm font-bold leading-100 text-black">
              {selectedMarket}
            </span>
          </span>
        </span>
      }
    >
      {({ listboxId, close, listboxClassName }) => (
        <div
          id={listboxId}
          role="listbox"
          className={listboxClassName}
          aria-label={marketLabel}
        >
          {marketOptions.map((option, index) => {
            const isSelected = option.value === selectedMarket;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-test-id="strategy-benchmark-chart-market-option"
                data-test-market-id={option.value}
                className={classNames(
                  "strategy-benchmark-chart__select-option px-3.5 py-3 border-none bg-transparent cursor-pointer text-left outline-none hover:bg-gray-10 focus-visible:bg-gray-10 focus-visible:outline-none",
                  index < marketOptions.length - 1 &&
                    "border-bottom border-bottom-gray-2"
                )}
                onClick={() => handleSelect(option.value, close)}
              >
                <span className="strategy-benchmark-chart__select-option-label">
                  <MarketFlag marketId={option.value} />
                  <span className="text-sm font-bold leading-100 text-black truncate">
                    {option.label}
                  </span>
                </span>
                <span className="strategy-benchmark-chart__select-option-rate">
                  <MarketRate
                    rate={option.rateOfReturn}
                    locale={locale}
                    isLoading={option.isLoading}
                  />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </StrategyBenchmarkChartOptionSelect>
  );
};

StrategyBenchmarkChartMarketPicker.propTypes = {
  locale: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  selectedMarket: PropTypes.string.isRequired,
  marketOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      rateOfReturn: PropTypes.number,
      isLoading: PropTypes.bool
    })
  ).isRequired,
  onMarketChange: PropTypes.func.isRequired
};

export default StrategyBenchmarkChartMarketPicker;
